import {
    Button,
    FormLabel,
    OptionType,
    SearchableSelect,
    Textinput,
    Tumbler
} from "shared";
import {
    ComponentTypes,
    IAutonumberCounter,
    ITemplateAutonumberComponent,
    ITemplateComponent
} from "entities/template";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAutonumberCounters } from "../../../../lib/hooks/useAutonumberCounters";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { useComponentValidateFunction } from "../../../../lib/hooks/useComponentValidateFunction";
import {
    autonumberCounterToComponent,
    DEFAULT_AUTONUMBER_CODE,
    INITIAL_AUTONUMBER_MASK,
    insertAutonumberToken,
    isAutonumberCounterNameTaken,
    isAutonumberDateSourceMissing,
    isAutonumberTagDuplicated,
    renderAutonumberPreview,
    validateAutonumberCode,
    validateAutonumberMaskSyntax
} from "../../../../lib/utils/autonumber";
import styles from "./component_form_autonumber.module.scss";
import { CounterAuthor } from "./counter_author/counter_author";

interface IComponentFormAutonumberProps {
    component: ITemplateAutonumberComponent;
    componentList: ITemplateComponent[];
    onChange: (component: ITemplateComponent) => void;
    triggerValidate: number;
}

const MASK_TOKENS = [
    { labelKey: "autonumber.tokenNumber", value: "{{number}}" },
    { labelKey: "autonumber.tokenCode", value: "{{code}}" },
    { labelKey: "autonumber.tokenDay", value: "{{day}}" },
    { labelKey: "autonumber.tokenMonth", value: "{{month}}" },
    { labelKey: "autonumber.tokenYear", value: "{{year}}" }
];

export const ComponentFormAutonumber: React.FC<IComponentFormAutonumberProps> = React.memo(({
    component,
    componentList,
    onChange,
    triggerValidate
}) => {
    const t = useComponentsTranslations();
    const { validateTag } = useComponentValidateFunction();
    const { counters, allCounters, loading, error, reload } = useAutonumberCounters();
    const [counterInput, setCounterInput] = useState("");
    const counterIdentity = `${component.id}:${component.autonumberId ?? "draft"}`;
    const counterIdentityRef = useRef(counterIdentity);
    const maskRef = useRef<HTMLInputElement>(null);
    const lastFocusedMaskRef = useRef<HTMLInputElement | null>(null);
    const candidateName = counterInput || component.counterName;
    const matchingOwnCounter = counters.find(counter => counter.name === candidateName.trim());
    const nameTaken = isAutonumberCounterNameTaken(
        candidateName,
        allCounters,
        counterInput ? undefined : component.autonumberId
    );
    const counterNameError = nameTaken && !(counterInput && matchingOwnCounter)
        ? t("autonumber.errors.name-taken", { name: candidateName.trim() })
        : undefined;

    useEffect(() => {
        if (counterIdentityRef.current !== counterIdentity) {
            counterIdentityRef.current = counterIdentity;
            lastFocusedMaskRef.current = null;
            setCounterInput("");
        }
    }, [counterIdentity]);

    const onFieldChange = (value: Partial<ITemplateAutonumberComponent>) => {
        onChange({ ...component, ...value });
    };

    const counterOptions: OptionType[] = useMemo(() => counters.map(counter => ({
        value: String(counter.id),
        label: counter.name,
        counter
    })), [counters]);

    const dateSourceMissing = isAutonumberDateSourceMissing(component, componentList);
    const counterSelectionDisabled = Boolean(component.counterReadOnly || component.counterLoadError === "forbidden");
    const counterSettingsDisabled = !component.counterName.trim() || counterSelectionDisabled;
    const counterAuthorLogin = component.counterReadOnly ? component.initialCounter?.login : undefined;

    const dateOptions: OptionType[] = useMemo(() => {
        const options: OptionType[] = [{ value: "current", label: t("autonumber.currentDate") }];
        componentList
            .filter(item => item.type === ComponentTypes.dateInput)
            .forEach(item => options.push({
                value: String(item.id),
                label: item.label || item.name || t("autonumber.date")
            }));
        if (dateSourceMissing) {
            options.push({
                value: "missing",
                label: component.dateTag || t("autonumber.missingDate")
            });
        }
        return options;
    }, [component.dateTag, componentList, dateSourceMissing, t]);

    const maskValidation = (value: string) => {
        const result = validateAutonumberMaskSyntax(value);
        return {
            valid: result.valid,
            message: result.error ? t(`autonumber.errors.${result.error}` as any) : undefined
        };
    };
    const codeValidation = (value: string) => {
        const result = validateAutonumberCode(component.mask, value);
        return result.valid ? result : {
            valid: false,
            message: t("autonumber.errors.code-required")
        };
    };
    const validateAutonumberTag = (value: string) => {
        const tagValidation = validateTag(value);
        if (!tagValidation.valid) {
            return tagValidation;
        }

        return isAutonumberTagDuplicated({ ...component, name: value }, componentList)
            ? { valid: false, message: t("autonumber.errors.duplicate-tag") }
            : { valid: true };
    };
    const previewMaskValidation = validateAutonumberMaskSyntax(component.mask);
    const previewCodeValidation = validateAutonumberCode(component.mask, component.code);
    const previewError = !previewMaskValidation.valid
        ? t(`autonumber.errors.${previewMaskValidation.error}` as any)
        : !previewCodeValidation.valid
            ? t("autonumber.previewCodeRequired")
            : dateSourceMissing
                ? t("autonumber.counterLoadErrors.date-not-found")
                : undefined;

    const onCounterChange = (option: OptionType | OptionType[] | null) => {
        if (counterSelectionDisabled || !option || Array.isArray(option)) {
            return;
        }
        lastFocusedMaskRef.current = null;
        const selectedCounter = option.counter as IAutonumberCounter | undefined;
        if (selectedCounter) {
            const selectedComponent = autonumberCounterToComponent(selectedCounter, component, componentList);
            onChange(selectedComponent);
            return;
        }
        onChange({
            ...component,
            autonumberId: undefined,
            counterName: option.value.trim(),
            name: "",
            mask: INITIAL_AUTONUMBER_MASK,
            code: DEFAULT_AUTONUMBER_CODE,
            dateComponentId: undefined,
            dateTag: undefined,
            isAttrUsed: false,
            initialCounter: undefined,
            counterReadOnly: false,
            counterLoadError: undefined
        });
    };

    const onDateChange = (value: string | string[] | null) => {
        if (value === "missing") {
            return;
        }
        const dateComponentId = typeof value === "string" && !["current", "missing"].includes(value)
            ? Number(value)
            : undefined;
        const dateComponent = componentList.find(item =>
            item.type === ComponentTypes.dateInput && item.id === dateComponentId
        );
        onFieldChange({
            dateComponentId,
            dateTag: dateComponent?.name,
            counterLoadError: component.counterLoadError === "date-not-found"
                ? undefined
                : component.counterLoadError
        });
    };

    const insertToken = (token: string) => {
        // The input retains its selection after focus moves to a token button.
        const maskHasSelection = maskRef.current !== null && maskRef.current === lastFocusedMaskRef.current;
        const start = maskHasSelection ? maskRef.current?.selectionStart ?? component.mask.length : component.mask.length;
        const end = maskHasSelection ? maskRef.current?.selectionEnd ?? start : start;
        const updatedMask = insertAutonumberToken(component.mask, token, start, end);
        onFieldChange({ mask: updatedMask });
        requestAnimationFrame(() => {
            maskRef.current?.focus();
            maskRef.current?.setSelectionRange(start + token.length, start + token.length);
        });
    };

    const selectedDateValue = dateSourceMissing
        ? "missing"
        : component.dateComponentId === undefined ? "current" : String(component.dateComponentId);
    return <div className={styles.container}>
        <div className={styles.description}>{t("autonumber.description")}</div>

        <FormLabel title={t("autonumber.counterName")} required={true}>
            <SearchableSelect
                id="autonumber-counter"
                value={component.counterName || null}
                options={counterOptions}
                loading={loading}
                isCreatable={true}
                isClearable={false}
                hideClearIcon={true}
                disabled={counterSelectionDisabled}
                validate={!component.counterReadOnly}
                triggerValidate={triggerValidate}
                errorMessage={counterNameError}
                menuPlacement="bottom"
                menuIsOpen={nameTaken && !matchingOwnCounter ? false : undefined}
                onInputChange={setCounterInput}
                isValidNewOption={value => Boolean(value.trim()) &&
                    !isAutonumberCounterNameTaken(value, allCounters)}
                onOptionChange={onCounterChange}
            />
            {counterAuthorLogin
                ? <CounterAuthor login={counterAuthorLogin} className={styles.subLabel}/>
                : <div className={styles.subLabel}>{t("autonumber.counterNameHint")}</div>}
            {!loading && !error && counters.length === 0 && !component.counterName &&
                <div className={styles.subLabel}>{t("autonumber.empty")}</div>}
            {error && <div className={styles.errorRow}>
                <span>{t("autonumber.loadError")}</span>
                <Button onClick={reload}>{t("autonumber.retry")}</Button>
            </div>}
        </FormLabel>

        {component.counterLoadError && component.counterLoadError !== "date-not-found" && <div className={styles.error}>
            {t(`autonumber.counterLoadErrors.${component.counterLoadError}` as any)}
        </div>}

        <FormLabel title={t("autonumber.tag")} required={true}>
            <Textinput
                id="autonumber-tag"
                size="m"
                view="outline"
                value={component.name}
                validate={!counterSettingsDisabled}
                triggerValidate={triggerValidate}
                validateFunc={validateAutonumberTag}
                disabled={counterSettingsDisabled}
                onChange={event => onFieldChange({ name: event.target.value })}
            />
        </FormLabel>

        <div>
            <div className={styles.grouping} data-testid="autonumber-grouping">
                <Tumbler
                    id="autonumber-group-by-legal-yandex"
                    className={styles.groupingTumbler}
                    size="m"
                    checked={component.isAttrUsed}
                    disabled={counterSettingsDisabled}
                    onChange={() => onFieldChange({ isAttrUsed: !component.isAttrUsed })}
                />
                <div className={styles.groupingText}>
                    <div className={styles.groupingTitle}>{t("autonumber.groupByLegalYandex")}</div>
                    <div className={styles.groupingHint}>
                        {t(component.isAttrUsed ? "autonumber.groupByLegalYandexSeparate" : "autonumber.groupByLegalYandexShared")}
                    </div>
                </div>
            </div>
            <div className={styles.groupingSource}>{t("autonumber.groupByLegalYandexSource")}</div>
        </div>

        <div className={styles.formatSection}>
            <FormLabel title={t("autonumber.format")} required={true}>
                <Textinput
                    id="autonumber-mask"
                    size="m"
                    view="outline"
                    controlRef={maskRef}
                    value={component.mask}
                    placeholder={t("autonumber.maskPlaceholder")}
                    validate={!counterSettingsDisabled}
                    triggerValidate={triggerValidate}
                    validateFunc={maskValidation}
                    disabled={counterSettingsDisabled}
                    onFocus={event => { lastFocusedMaskRef.current = event.target; }}
                    onChange={event => onFieldChange({ mask: event.target.value })}
                />
                <div className={styles.subLabel}>{t("autonumber.formatHint")}</div>
                <div className={styles.tokens}>
                    {MASK_TOKENS.map(token => <Button
                        key={token.labelKey}
                        size="s"
                        disabled={counterSettingsDisabled}
                        onClick={() => insertToken(token.value)}
                    >
                        {t(token.labelKey as any)}
                    </Button>)}
                </div>
            </FormLabel>

            {component.mask.includes("{{code}}") && <FormLabel title={t("autonumber.code")} required={true}>
                <Textinput
                    id="autonumber-code"
                    size="m"
                    view="outline"
                    value={component.code || ""}
                    placeholder={t("autonumber.codePlaceholder")}
                    validate={!counterSettingsDisabled}
                    triggerValidate={triggerValidate}
                    validateFunc={codeValidation}
                    disabled={counterSettingsDisabled}
                    onChange={event => onFieldChange({ code: event.target.value })}
                />
                <div className={styles.subLabel}>{t("autonumber.codeHint")}</div>
            </FormLabel>}

            <FormLabel title={t("autonumber.date")} required={false}>
                <SearchableSelect
                    id="autonumber-date"
                    value={selectedDateValue}
                    options={dateOptions}
                    isClearable={false}
                    hideClearIcon={true}
                    disabled={counterSettingsDisabled}
                    onChange={onDateChange}
                />
                <div className={styles.subLabel}>{t("autonumber.dateHint")}</div>
                {dateSourceMissing && <div className={styles.error}>
                    {t("autonumber.counterLoadErrors.date-not-found")}
                </div>}
            </FormLabel>

            <div
                className={`${styles.example}${previewError ? ` ${styles.exampleError}` : ""}`}
                data-testid="autonumber-example"
                aria-live="polite"
                aria-atomic="true"
            >
                <div className={styles.exampleTitle}>
                    {previewError ? t("autonumber.previewUnavailableTitle") : t("autonumber.previewTitle")}
                </div>
                {previewError
                    ? <div className={styles.exampleDescription}>{previewError}</div>
                    : <output className={styles.exampleValue}>
                        {renderAutonumberPreview(component.mask, { number: 1, code: component.code })}
                    </output>}
            </div>
        </div>
    </div>;
});
